import { query } from '@portal/utils/util-dom';
import { guardEmptyString, guardMinLengthString } from '@portal/utils/util-guards';
import { componentFactoryOf } from 'vue-tsx-support';
import { useModule } from 'vuex-simple';

import { JtnUiInput, JtnUiBtn, JtnUiBtnIcon } from '@jtnews/jtn-ui';
import { injectStylesMixin, ObserverVisibility } from '@jtnews/shared';
import { EMAIL_REGEX, HTML_TAGS_REGEX } from '@jtnews/shared/data';

import { RECORD_MODULE_NAMESPACE, RecordModule } from '../../store';

import styles from './record-theme-subscriptions.style.scss?module';

const EMAIL_MIN_LENGTH_ERROR = 0;
const EMAIL_EMPTY_ERROR = 'Введите E-mail';
const EMAIL_REGEX_ERROR = 'Введите верный E-mail';

type ComponentData = {
  visibility$: ObserverVisibility;
  email: string;
  apiErrorText: string;
  isShowForm: boolean;
  isSubscribeSuccess: boolean;
  isTriedSubmit: boolean;
};

interface Events {
  onVisible: void;
  onSubscribeSuccess: void;
  onSubscribeTgBtnClicked: void;
  onShowFormBtnClicked: void;
  onCloseFormBtnClicked: void;
  onChangedForm: void;
}

const ICON_SIZE = 24;
const CLOSE_ICON_SIZE = 18;
const RESET_ICON_SIZE = 16;

export default componentFactoryOf<Events>()
  .mixin(injectStylesMixin(styles))
  .create({
    name: 'RecordThemeSubscription',
    props: {
      caption: {
        type: String,
        default: ''
      },
      title: {
        type: String,
        default: ''
      },
      description: {
        type: String,
        default: ''
      },
      note: {
        type: String,
        default: ''
      },
      telegramLink: {
        type: String,
        default: ''
      },
      subscriptionTypeId: {
        type: String,
        default: ''
      },
      isMobile: {
        type: Boolean,
        default: false
      }
    },
    data(): ComponentData {
      return {
        visibility$: new ObserverVisibility('half'),
        email: '',
        apiErrorText: '',
        isShowForm: false,
        isTriedSubmit: false,
        isSubscribeSuccess: false
      };
    },
    computed: {
      recordModule(): RecordModule | undefined {
        return useModule<RecordModule>(this.$store, RECORD_MODULE_NAMESPACE);
      },
      hasTelegramBtn(): boolean {
        return guardEmptyString(this.telegramLink) && (this.isMobile || !this.isShowForm);
      },
      subscribeEmailBtnTheme(): 'lightGray' | 'primary' {
        return guardEmptyString(this.telegramLink) ? 'lightGray' : 'primary';
      },
      emailSubscribeText(): string {
        return guardEmptyString(this.telegramLink)
          ? 'подписаться на e-mail'
          : 'подписаться';
      },
      emailErrorText(): string {
        if (!this.isTriedSubmit) {
          return '';
        }

        if (!guardMinLengthString(this.email.trim(), EMAIL_MIN_LENGTH_ERROR)) {
          return EMAIL_EMPTY_ERROR;
        }

        if (!EMAIL_REGEX.test(this.email) || HTML_TAGS_REGEX.exec(this.email) !== null) {
          return EMAIL_REGEX_ERROR;
        }

        return '';
      },
      isShowApiError(): boolean {
        return guardEmptyString(this.apiErrorText);
      }
    },
    mounted() {
      this.visibility$.event.subscribe(state => {
        if (state.value) {
          this.$emit('visible');
        }
      });

      this.$el.addEventListener('input', () => this.$emit('changedForm'), { once: true });
    },
    beforeDestroy() {
      this.visibility$.destroy();
    },
    methods: {
      async onSubmit(event: Event) {
        event.preventDefault();
        this.isTriedSubmit = true;

        if (guardEmptyString(this.emailErrorText)) {
          return;
        }

        this.apiErrorText = '';
        try {
          await this.recordModule?.subscribeToThemeRecord({
            email: this.email,
            type: this.subscriptionTypeId
          });
          this.isSubscribeSuccess = true;
          this.$emit('subscribeSuccess');
        } catch (err) {
          this.apiErrorText = err as string;
        }
      },
      clickedShowFormBtn() {
        this.isShowForm = true;
        this.$emit('showFormBtnClicked');
        this.$nextTick(() => {
          const inputEl = query('input', (this.$refs.subscriptionInput as Vue)?.$el);
          (inputEl as HTMLInputElement)?.focus();
        });
      },
      closeForm() {
        this.email = '';
        this.apiErrorText = '';
        this.isShowForm = false;
        this.isTriedSubmit = false;
        this.isSubscribeSuccess = false;
        this.$emit('closeFormBtnClicked');
      }
    },
    render() {
      return (
        <section
          class={styles.recordThemeSubscription}
          v-observe-visibility={this.visibility$.getOptions('half')}
        >
          <p class={styles.caption}>{this.caption}</p>
          <p class={styles.title}>{this.title}</p>
          {guardEmptyString(this.description) && (
            <p class={styles.description}>{this.description}</p>
          )}

          <div class={styles.actions}>
            {this.hasTelegramBtn && (
              <JtnUiBtn
                href={this.telegramLink}
                class={styles.subscribeBtn}
                target="_blank"
                onClick={() => this.$emit('subscribeTgBtnClicked')}
              >
                <template slot="left">
                  <svg fill="currentColor" width={ICON_SIZE} height={ICON_SIZE}>
                    <use xlinkHref="/dist/legacy/svg-sprites/jtn-social.478c99898e9d4753ae7bc5e64d408199.svg#jtn-social-telegram"></use>
                  </svg>
                </template>
                подписаться в telegram
                <template slot="right">
                  <svg width={ICON_SIZE} height={ICON_SIZE}>
                    <use xlinkHref="/dist/legacy/svg-sprites/jtn-critical.42b7545660e4f467e75d4b37a02533e6.svg#jtn-critical-arrow-line-right"></use>
                  </svg>
                </template>
              </JtnUiBtn>
            )}
            {!this.isShowForm ? (
              <JtnUiBtn
                class={styles.subscribeBtn}
                theme={this.subscribeEmailBtnTheme}
                onClick={() => this.clickedShowFormBtn()}
              >
                <svg slot="left" width={ICON_SIZE} height={ICON_SIZE}>
                  <use xlinkHref="/dist/legacy/svg-sprites/jtn-critical.42b7545660e4f467e75d4b37a02533e6.svg#jtn-critical-email"></use>
                </svg>
                <span>{this.emailSubscribeText}</span>
                <svg slot="right" width={ICON_SIZE} height={ICON_SIZE}>
                  <use xlinkHref="/dist/legacy/svg-sprites/jtn-critical.42b7545660e4f467e75d4b37a02533e6.svg#jtn-critical-arrow-line-right"></use>
                </svg>
              </JtnUiBtn>
            ) : !this.isSubscribeSuccess ? (
              <form class={styles.form} onSubmit={e => void this.onSubmit(e)}>
                <svg class={styles.icon} width={ICON_SIZE} height={ICON_SIZE}>
                  <use xlinkHref="/dist/legacy/svg-sprites/jtn-critical.42b7545660e4f467e75d4b37a02533e6.svg#jtn-critical-email"></use>
                </svg>
                <JtnUiInput
                  ref="subscriptionInput"
                  class={styles.field}
                  placeholder="Введите e-mail"
                  value={this.email}
                  has-error={this.emailErrorText.length > 0}
                  data-test="subscription-input"
                  onInput={(event: { target: HTMLInputElement }) =>
                    (this.email = event.target.value)
                  }
                >
                  {guardEmptyString(this.emailErrorText) && (
                    <span slot="error" class={styles.errorInput}>
                      {this.emailErrorText}
                    </span>
                  )}
                </JtnUiInput>
                <div class={styles.fieldActions}>
                  <JtnUiBtnIcon
                    class={styles.resetBtn}
                    btnType="button-other-type-3"
                    size={ICON_SIZE}
                    type="reset"
                    onClick={() => this.closeForm()}
                  >
                    <svg
                      fill="currentColor"
                      width={RESET_ICON_SIZE}
                      height={RESET_ICON_SIZE}
                    >
                      <use xlinkHref="/dist/legacy/svg-sprites/jtn-critical.42b7545660e4f467e75d4b37a02533e6.svg#jtn-critical-close"></use>
                    </svg>
                  </JtnUiBtnIcon>
                  <JtnUiBtn class={styles.submitBtn} type="submit">
                    <svg slot="left" width={ICON_SIZE} height={ICON_SIZE}>
                      <use xlinkHref="/dist/legacy/svg-sprites/jtn-critical.42b7545660e4f467e75d4b37a02533e6.svg#jtn-critical-arrow-line-right"></use>
                    </svg>
                  </JtnUiBtn>
                </div>
                {!guardEmptyString(this.emailErrorText) && (
                  <p class={styles.note}>{this.note}</p>
                )}
              </form>
            ) : (
              <p class={styles.successText}>Спасибо! Проверьте почту</p>
            )}
          </div>

          {this.isShowApiError && (
            <div class={styles.apiErrorWrap}>
              <div class={styles.modal}>
                <JtnUiBtnIcon
                  class={styles.closeBtn}
                  btnType="button-other-type-3"
                  size={ICON_SIZE}
                  onClick={() => (this.apiErrorText = '')}
                >
                  <svg
                    fill="currentColor"
                    width={CLOSE_ICON_SIZE}
                    height={CLOSE_ICON_SIZE}
                  >
                    <use xlinkHref="/dist/legacy/svg-sprites/jtn-critical.42b7545660e4f467e75d4b37a02533e6.svg#jtn-critical-close"></use>
                  </svg>
                </JtnUiBtnIcon>
                <div class={styles.modalCaption}>
                  <svg stroke-width="2" width={ICON_SIZE} height={ICON_SIZE}>
                    <use xlinkHref="/dist/legacy/svg-sprites/jtn-common.f7875ac1d2b1e964bfa6c21ef5757c8a.svg#jtn-common-attention"></use>
                  </svg>
                  <span>Произошла ошибка</span>
                </div>
                <div class={styles.modalText} domPropsInnerHTML={this.apiErrorText} />
              </div>
            </div>
          )}
        </section>
      );
    }
  });
